import type { RichTextSelection } from '@/shared/ui';

/** Участок текста результата: с какого места, сколько знаков и чем заменить. */
export type ChartTemplateEdit = {
  at: number;
  length: number;
  text: string;
};

/**
 * Пометка начала повторяемой части.
 *
 * Единственное описание её вида: по нему пометка и распознаётся в тексте, и ставится
 * ({@link ROW_SECTION_OPEN}). Имя внутри пометки ничего не различает — строки выборки одни на весь
 * текст, — поэтому вид имени не задан: сервер принимает и пометку, набранную пользователем иначе.
 */
const ROW_SECTION_OPEN_PATTERN = /\{[*#][^{}]*\}/;

/** Пометка конца повторяемой части — единственное описание её вида. */
const ROW_SECTION_CLOSE_PATTERN = /\{\/[^{}]*\}/;

/** Пометка начала повторяемой части в том виде, в каком её ставит кнопка и пишет составитель отбора. */
export const ROW_SECTION_OPEN = '{*строки}';

/** Пометка конца повторяемой части — пара к {@link ROW_SECTION_OPEN}. */
export const ROW_SECTION_CLOSE = '{/строки}';

const ROW_SECTION_MARKER_PATTERN = new RegExp(
  `${ROW_SECTION_OPEN_PATTERN.source}|${ROW_SECTION_CLOSE_PATTERN.source}`,
  'g',
);

/**
 * Конец текста без завершающего переноса строки.
 *
 * Свой последний перенос редактор считает частью текста, но за ним ничего нет: правка,
 * поставленная после него, ушла бы в строку, которой пользователь не видит.
 */
const textEndOf = (text: string): number => text.replace(/\n$/, '').length;

/** Границы строк, занятых выделением: повторяется целый абзац, а не его середина. */
const wholeLinesAround = (text: string, selection: RichTextSelection | null) => {
  const textEnd = textEndOf(text);
  if (!selection) return { start: 0, end: textEnd };

  const from = Math.min(selection.index, textEnd);
  const to = Math.min(selection.index + selection.length, textEnd);
  const lineEnd = text.indexOf('\n', to);

  return {
    start: text.lastIndexOf('\n', from - 1) + 1,
    end: lineEnd === -1 ? textEnd : Math.min(lineEnd, textEnd),
  };
};

/** Есть ли в тексте результата повторяемая часть. */
export const hasRowSection = (template: string): boolean => ROW_SECTION_OPEN_PATTERN.test(template);

/**
 * Правки, размечающие выделенное как повторяемую часть, — в порядке применения.
 *
 * Каждая пометка встаёт своей строкой: строку с одной лишь пометкой сервер уносит вместе с ней, и
 * повторы идут абзац за абзацем, а не слипаются в один. Помечается целая строка, даже если выделена
 * её середина, — повтор половины абзаца разорвал бы оформление сводки. Без известного выделения
 * повторяемым становится весь текст: какую его часть имел в виду пользователь, взять неоткуда.
 *
 * Правки идут от конца текста к началу — иначе первая же вставка сдвинула бы место второй.
 */
export const rowSectionEdits = (text: string, selection: RichTextSelection | null): ChartTemplateEdit[] => {
  const { start, end } = wholeLinesAround(text, selection);

  return [
    { at: end, length: 0, text: `\n${ROW_SECTION_CLOSE}` },
    { at: start, length: 0, text: `${ROW_SECTION_OPEN}\n` },
  ];
};

/**
 * Правки, снимающие пометки повторяемой части, — в порядке применения (от конца текста к началу).
 *
 * Пометка, занявшая строку целиком, уходит вместе с ней: иначе на её месте осталась бы пустая
 * строка. Пометка, стоящая среди текста, уносит только себя.
 */
export const rowSectionRemovalEdits = (text: string): ChartTemplateEdit[] => {
  const textEnd = textEndOf(text);

  return [...text.matchAll(ROW_SECTION_MARKER_PATTERN)].reverse().map((match) => {
    const start = match.index;
    const end = start + match[0].length;
    const isOwnLine = (start === 0 || text[start - 1] === '\n') && (end >= textEnd || text[end] === '\n');

    if (!isOwnLine) return { at: start, length: match[0].length, text: '' };

    return text[end] === '\n'
      ? { at: start, length: end - start + 1, text: '' }
      : { at: Math.max(start - 1, 0), length: end - Math.max(start - 1, 0), text: '' };
  });
};
